import { RegisterImplementationProps } from 'constants/types';
import SignInBase from '../SignInBase';
import MainContainer from '../SignInMainContainer';
import LoaderButton from 'components/Button/LoaderButton';
import Input from 'components/Input/Input';
import PasswordInput from 'components/Input/PasswordInput';
import { credentialInstructions, getProviderFormLabel } from '../utils';
import NoticeMessage from 'pages/Authorize/Messages/Notice';
import { AppLink } from 'components/Link/AppLink';
import { useAuthorizeContext } from 'pages/Authorize/AuthorizeContext';
import { useCameFromAuthFallback } from 'store/came-from-auth-fallback';
import { step } from 'pages/Authorize/steps';
import { Box } from '@chakra-ui/react';

const SignInQuickbooksCredential = ({
  isLoading,
  onSubmit,
  client,
  provider,
}: RegisterImplementationProps) => {
  const instructions = credentialInstructions(client);
  const { setCurrentStep } = useAuthorizeContext() || {};
  const setCameFromAuthFallback = useCameFromAuthFallback(
    (state) => state.setCameFromAuthFallback,
  );

  // Passkey-protected QuickBooks accounts can never complete automated sign-in
  // (FIDO2 only, no email/SMS/TOTP), so we let employers opt into the auth
  // fallback (manual sign in) upfront instead of failing first and being
  // redirected there by the REDIRECT_TO_AUTH_FALLBACK hint.
  const goToAuthFallback = () => {
    // Mark the flow as originating from auth fallback so the downstream manual
    // sign-in POST sends acfFallbackEligible=true. Without this, api-server
    // mints a fully assisted (manual=true) token instead of an automated
    // auth-fallback (manual=false) one.
    setCameFromAuthFallback(true);
    setCurrentStep?.(step.MANUAL_SIGN_IN);
  };

  return (
    <SignInBase
      formLabel={getProviderFormLabel(provider)}
      instructions={instructions}
      onSubmit={onSubmit}
      fieldNames={['username', 'password']}
      render={({ username, password }) => {
        return (
          <>
            <MainContainer useNewDesign>
              <Box pb="1px">
                <NoticeMessage>
                  Please ensure you have an open seat available in your
                  QuickBooks Online account so that a third-party admin can be
                  added.
                </NoticeMessage>
              </Box>
              <Box pb="1px">
                <NoticeMessage>
                  Using a <b>passkey</b> to sign in to QuickBooks?
                  Passkey-protected accounts aren&apos;t supported for automated
                  sign-in.{' '}
                  <AppLink as="button" type="button" onClick={goToAuthFallback}>
                    Connect manually instead
                  </AppLink>
                  .
                </NoticeMessage>
              </Box>
              <Input
                value={username.value}
                onChange={(event) => username.onChange(event.target.value)}
                invalid={username.invalid}
                label="User ID"
                id="username"
              />
              <PasswordInput
                reset={{
                  uri: new URL(
                    'https://accounts.intuit.com/app/sign-in?app_group=Identity&asset_alias=Intuit.cto.iam.ius&start_screen_hint=AccountRecovery',
                  ),
                  providerDisplayName: provider.displayName,
                }}
                value={password.value}
                onChange={(event) => password.onChange(event.target.value)}
                invalid={password.invalid}
              />
            </MainContainer>
            <LoaderButton
              isLoading={isLoading}
              disabled={
                isLoading || !username.value.trim() || !password.value.trim()
              }
              type="submit"
              text="Connect"
            />
          </>
        );
      }}
    />
  );
};

export default SignInQuickbooksCredential;
